interface StatusBadgeProps {
  status: string;
  paymentStatus?: string;
}

interface PaymentBadgeProps {
  status: string;
}

/**
 * Smart status badge — metallic gray aesthetic
 * - cancelled + payment_status='failed' → red-orange: "Pago fallido"
 * - cancelled                           → dark gray + subtle red tint
 * - confirmed                           → light gray background + dark text
 * - pending                             → medium gray background
 * - no_show                             → amber/orange tint
 * - completed                           → emerald tint
 */
export function StatusBadge({ status, paymentStatus }: StatusBadgeProps) {
  // Special case: cancelled due to failed payment — show a distinct badge
  const isCancelledByFailedPayment = status === 'cancelled' && paymentStatus === 'failed';
  const resolved = isCancelledByFailedPayment ? 'payment_failed' : resolveStatus(status);

  const styles: Record<string, string> = {
    confirmed:       'bg-[#D9D9D9]/12 text-[#D9D9D9] border border-[#D9D9D9]/25',
    waiting:         'bg-[#888888]/15 text-[#A0A0A0] border border-[#888888]/25',
    cancelled:       'bg-red-900/20 text-red-400/80 border border-red-800/30',
    payment_failed:  'bg-orange-900/25 text-orange-400 border border-orange-700/35',
    no_show:         'bg-amber-500/12 text-amber-400/90 border border-amber-500/25',
    completed:       'bg-emerald-500/12 text-emerald-400/90 border border-emerald-500/25',
    unknown:         'bg-white/6 text-white/35 border border-white/10',
  };

  const icons: Record<string, string> = {
    confirmed:      'ri-checkbox-circle-line',
    waiting:        'ri-time-line',
    cancelled:      'ri-close-circle-line',
    payment_failed: 'ri-bank-card-2-line',
    no_show:        'ri-user-unfollow-line',
    completed:      'ri-check-double-line',
    unknown:        'ri-question-line',
  };

  const labels: Record<string, string> = {
    confirmed:      'Confirmada',
    waiting:        'Esperando pago',
    cancelled:      'Cancelada',
    payment_failed: 'Pago fallido',
    no_show:        'No se presentó',
    completed:      'Completada',
    unknown:        status,
  };

  const key = resolved;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${styles[key] ?? styles.unknown}`}
    >
      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
        <i className={`${icons[key] ?? icons.unknown} text-[10px]`} />
      </div>
      {labels[key] ?? labels.unknown}
    </span>
  );
}

function resolveStatus(status: string): string {
  if (status === 'cancelled') return 'cancelled';
  if (status === 'confirmed') return 'confirmed';
  if (status === 'pending') return 'waiting';
  if (status === 'no_show') return 'no_show';
  if (status === 'completed') return 'completed';
  return 'unknown';
}

/**
 * Payment status badge — metallic gray aesthetic
 */
export function PaymentBadge({ status }: PaymentBadgeProps) {
  const map: Record<string, { label: string; icon: string; classes: string }> = {
    paid:     { label: 'Pagado',       icon: 'ri-checkbox-circle-line',  classes: 'bg-emerald-500/10 text-emerald-400/80 border border-emerald-500/20' },
    pending:  { label: 'Pendiente',    icon: 'ri-time-line',             classes: 'bg-[#888]/10 text-[#A0A0A0] border border-[#888]/20' },
    failed:   { label: 'Pago fallido', icon: 'ri-bank-card-2-line',      classes: 'bg-orange-900/20 text-orange-400 border border-orange-700/30' },
    refunded: { label: 'Reembolsado',  icon: 'ri-refund-2-line',         classes: 'bg-white/6 text-white/40 border border-white/10' },
  };
  const cfg = map[status] ?? { label: status, icon: 'ri-question-line', classes: 'bg-white/6 text-white/35 border border-white/10' };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cfg.classes}`}
    >
      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
        <i className={`${cfg.icon} text-[10px]`} />
      </div>
      {cfg.label}
    </span>
  );
}
